/**
 * Conductor frontend error auto-report (P-COND-B401988D766F PI-ST9, plan D4).
 *
 * Sends uncaught errors (`window.onerror`, `unhandledrejection`) and explicit
 * `reportClientError()` calls (e.g. `App.tsx::ChunkErrorBoundary`) to
 * `POST /api/v1/client-errors` (`backend/app/api/client_errors.py`), where they
 * become agent issues with `capture_source="frontend"` (观测中心 来源=前端).
 *
 * Deliberately NOT `services/api.ts` ApiClient: its 401 interceptor clears auth
 * and redirects to /login — an error report must never log the user out. This
 * module uses its own `fetch`, with the same API base URL
 * (`REACT_APP_API_URL`: `/conductor` on the sg build, empty on the root-based cn
 * build) and the same token storage helper (`authStorage.ts::getAuthItem`).
 *
 * Fire-and-forget: never throws, never retries, never awaits in callers.
 */
import { getAuthItem } from './authStorage';

/**
 * Client-side limits — the only definition site on the frontend. Byte caps
 * mirror `client_errors.py::CLIENT_ERROR_*_MAX_BYTES`; we truncate before
 * sending so a long stack is shortened rather than rejected with 422.
 */
export const REPORT_LIMITS = {
  maxPerPageLoad: 10,
  fingerprintWindowMs: 60_000,
  messageBytes: 2 * 1024,
  stackBytes: 8 * 1024,
  contextBytes: 16 * 1024,
  urlBytes: 2 * 1024,
  userAgentBytes: 512,
  fingerprintBytes: 128,
} as const;

export const CLIENT_ERRORS_PATH = '/api/v1/client-errors';

let sentThisPage = 0;
let stopped = false; // 401 / 404 disabled / 429 budget gone → quiet for this page load
let installed = false;
const lastSentAt = new Map<string, number>();

/**
 * UTF-8 byte width of the code point starting at `s[i]` and its UTF-16 length.
 * Index-based (no string iteration / TextEncoder: es5 target, jsdom).
 */
function codePointAt(s: string, i: number): { bytes: number; units: number } {
  const c = s.charCodeAt(i);
  if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
    const d = s.charCodeAt(i + 1);
    if (d >= 0xdc00 && d <= 0xdfff) return { bytes: 4, units: 2 };
  }
  return { bytes: c < 0x80 ? 1 : c < 0x800 ? 2 : 3, units: 1 };
}

function utf8Length(s: string): number {
  let n = 0;
  for (let i = 0; i < s.length; ) {
    const cp = codePointAt(s, i);
    n += cp.bytes;
    i += cp.units;
  }
  return n;
}

/** Longest prefix of `s` whose UTF-8 encoding fits in `maxBytes`. */
export function truncateBytes(s: string, maxBytes: number): string {
  let n = 0;
  let i = 0;
  while (i < s.length) {
    const cp = codePointAt(s, i);
    if (n + cp.bytes > maxBytes) break;
    n += cp.bytes;
    i += cp.units;
  }
  return s.slice(0, i);
}

function hash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(16);
}

function describe(error: unknown): { message: string; stack: string } {
  if (error instanceof Error) {
    return { message: `${error.name}: ${error.message}`, stack: error.stack || '' };
  }
  if (typeof error === 'string') return { message: error, stack: '' };
  try {
    return { message: JSON.stringify(error) ?? String(error), stack: '' };
  } catch {
    return { message: String(error), stack: '' };
  }
}

function boundedContext(ctx?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!ctx) return undefined;
  try {
    const json = JSON.stringify(ctx);
    if (utf8Length(json) <= REPORT_LIMITS.contextBytes) return ctx;
    return {
      truncated: true,
      preview: truncateBytes(json, REPORT_LIMITS.contextBytes - 64),
    };
  } catch {
    return { unserializable: true };
  }
}

/** Report one error. Never throws; silently drops when not allowed to send. */
export function reportClientError(error: unknown, context?: Record<string, unknown>): void {
  try {
    if (stopped || sentThisPage >= REPORT_LIMITS.maxPerPageLoad) return;
    const token = getAuthItem('conductor_auth_token');
    if (!token) return; // not logged in → endpoint would 401; skip

    const { message: rawMessage, stack: rawStack } = describe(error);
    const message = truncateBytes(rawMessage || 'Unknown error', REPORT_LIMITS.messageBytes);
    const stack = truncateBytes(rawStack, REPORT_LIMITS.stackBytes);
    const firstFrame = stack.split('\n').find((l) => /\bat\b|@/.test(l)) || '';
    const fingerprint = truncateBytes(
      hash(`${message}|${firstFrame.trim()}`),
      REPORT_LIMITS.fingerprintBytes,
    );

    const now = Date.now();
    const prev = lastSentAt.get(fingerprint);
    if (prev !== undefined && now - prev < REPORT_LIMITS.fingerprintWindowMs) return;
    lastSentAt.set(fingerprint, now);
    sentThisPage += 1;

    const body = {
      message,
      stack: stack || undefined,
      context: boundedContext(context),
      url: truncateBytes(window.location.href, REPORT_LIMITS.urlBytes),
      user_agent: truncateBytes(navigator.userAgent || '', REPORT_LIMITS.userAgentBytes),
      fingerprint,
    };
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
    fetch(`${base}${CLIENT_ERRORS_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
      keepalive: true,
    })
      .then((resp) => {
        // 401 = token rejected (never clear auth / redirect — just go quiet),
        // 404 = feature disabled, 429 = hourly budget spent → stop for the rest
        // of this page load.
        if (resp.status === 401 || resp.status === 404 || resp.status === 429) stopped = true;
      })
      .catch(() => undefined);
  } catch {
    // A reporter failure must never surface as another error.
  }
}

function onWindowError(event: ErrorEvent): void {
  reportClientError(event.error ?? event.message, { kind: 'window.onerror' });
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  reportClientError(event.reason, { kind: 'unhandledrejection' });
}

/** Install the global listeners once (idempotent). Called from index.tsx. */
export function installErrorReporter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', onWindowError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
}

/** Test-only: reset module state and remove listeners. */
export function __resetErrorReporterForTests(): void {
  sentThisPage = 0;
  stopped = false;
  lastSentAt.clear();
  if (installed && typeof window !== 'undefined') {
    window.removeEventListener('error', onWindowError);
    window.removeEventListener('unhandledrejection', onUnhandledRejection);
  }
  installed = false;
}
